import type { Context, ReactElement, ReactNode } from "react";
import { createContext, createElement, useContext, useMemo } from "react";
import { getRuntimeConfig } from "@/app/config/runtime-config";
import {
  HttpRegistrationFinancialClient,
  type RegistrationFinancialClient,
} from "./registration-financial-client";

/**
 * Client injection for the CLMS-117 registration-financial surface (T033),
 * mirroring RegistrationInvitationClientProvider so fixture tests inject a
 * stub client without network access.
 */

const RegistrationFinancialClientContext: Context<RegistrationFinancialClient | undefined> =
  createContext<RegistrationFinancialClient | undefined>(undefined);

let defaultClient: RegistrationFinancialClient | undefined;

function getDefaultRegistrationFinancialClient(): RegistrationFinancialClient {
  defaultClient ??= new HttpRegistrationFinancialClient();
  return defaultClient;
}

export interface RegistrationFinancialClientProviderProps {
  children: ReactNode;
  client?: RegistrationFinancialClient;
}

export function RegistrationFinancialClientProvider({
  children,
  client,
}: RegistrationFinancialClientProviderProps): ReactElement {
  const value = useMemo(() => client ?? getDefaultRegistrationFinancialClient(), [client]);
  return createElement(RegistrationFinancialClientContext.Provider, { value }, children);
}

export function useRegistrationFinancialClient(): RegistrationFinancialClient {
  return useContext(RegistrationFinancialClientContext) ?? getDefaultRegistrationFinancialClient();
}

/** Contract defaults: 5-second poll interval, 2-minute total window. */
export const DEFAULT_STATUS_POLL_INTERVAL_MS = 5_000;
export const DEFAULT_STATUS_POLL_WINDOW_MS = 120_000;

export interface StatusPollingConfig {
  intervalMs: number;
  windowMs: number;
}

/** Runtime-configured polling bounds with contract defaults (UI contract). */
export function getStatusPollingConfig(): StatusPollingConfig {
  const configured = getRuntimeConfig().registrationPayments;
  return {
    intervalMs:
      configured?.statusPollIntervalSeconds !== undefined
        ? configured.statusPollIntervalSeconds * 1_000
        : DEFAULT_STATUS_POLL_INTERVAL_MS,
    windowMs:
      configured?.statusPollWindowSeconds !== undefined
        ? configured.statusPollWindowSeconds * 1_000
        : DEFAULT_STATUS_POLL_WINDOW_MS,
  };
}

/**
 * Fail-safe publishable-key resolution: absent/blank config means the payment
 * step must not render (the deferred surface shows instead) — never a crash.
 * Returns the trimmed key (parse normalizes; this guards non-parsed sources)
 * so Stripe.js loads correctly and the per-key loader cache never splits
 * across equivalent keys.
 */
export function getStripePublishableKey(): string | null {
  const key = getRuntimeConfig().registrationPayments?.stripePublishableKey?.trim();
  return key ? key : null;
}
